import { Card as CardType } from '../types';

export interface CreatePaymentIntentRequest {
  items: Array<{
    card: CardType;
    quantity: number;
  }>;
  customerEmail: string;
  shippingAddress: {
    name: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
}

export interface OrderConfirmation {
  orderId: string;
  paymentIntentId: string;
  status: 'succeeded' | 'failed' | 'processing';
  items: Array<{
    card: CardType;
    quantity: number;
  }>;
  total: number;
  customerEmail: string;
  shippingAddress: CreatePaymentIntentRequest['shippingAddress'];
}

class StripeService {
  private baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://api.goatcards.shop';

  async createPaymentIntent(request: CreatePaymentIntentRequest): Promise<CreatePaymentIntentResponse> {
    try {
      // Try common endpoint patterns - update this to match your actual API
      const response = await fetch(`${this.baseUrl}/api/v1/payments/payment-intents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: request.items.reduce((total, item) => total + (item.card.price * item.quantity), 0) * 100, // Convert to cents
          currency: 'usd',
          description: `Payment for ${request.items.length} item(s)`,
          metadata: {
            customerEmail: request.customerEmail,
            items: JSON.stringify(request.items),
            shippingAddress: JSON.stringify(request.shippingAddress)
          }
        }),
      });

      if (!response.ok) {
        // Check if response is HTML (error page)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error(`API server not running on ${this.baseUrl}. Please start your API server.`);
        }
        
        const error = await response.json();
        throw new Error(error.message || 'Failed to create payment intent');
      }

      const data = await response.json();
      return {
        clientSecret: data.clientSecret,
        paymentIntentId: data.id
      };
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to API server at ${this.baseUrl}. Please ensure your API server is running.`);
      }
      
      // For development: Return mock data when API is not available
      console.warn('API not available, using mock data for development');
      const mockId = 'pi_mock_' + Date.now();
      return {
        clientSecret: `${mockId}_secret_${Math.random().toString(36).substr(2, 24)}`,
        paymentIntentId: mockId
      };
    }
  }

  async confirmPayment(paymentIntentId: string): Promise<OrderConfirmation> {
    try {
      // Try common endpoint patterns - update this to match your actual API
      const response = await fetch(`${this.baseUrl}/api/v1/payments/payment-intents/${paymentIntentId}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentIntentId }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error(`API server not running on ${this.baseUrl}. Please start your API server.`);
        }
        
        const error = await response.json();
        throw new Error(error.message || 'Failed to confirm payment');
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to API server at ${this.baseUrl}. Please ensure your API server is running.`);
      }
      
      // For development: Return mock data when API is not available
      console.warn('API not available, using mock data for development');
      return {
        orderId: 'order_mock_' + Date.now(),
        paymentIntentId: paymentIntentId,
        status: 'succeeded',
        items: [], // Will be filled by the calling component
        total: 0, // Will be filled by the calling component
        customerEmail: 'demo@example.com',
        shippingAddress: {
          name: 'Demo User',
          line1: '123 Demo St',
          city: 'Demo City',
          state: 'DC',
          postalCode: '12345',
          country: 'US'
        }
      };
    }
  }

  async getOrderStatus(orderId: string): Promise<OrderConfirmation> {
    try {
      // Update this endpoint to match your existing API
      const response = await fetch(`${this.baseUrl}/api/orders/${orderId}`);

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error(`API server not running on ${this.baseUrl}. Please start your API server.`);
        }
        
        const error = await response.json();
        throw new Error(error.message || 'Failed to get order status');
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to API server at ${this.baseUrl}. Please ensure your API server is running.`);
      }
      throw error;
    }
  }
}

export const stripeService = new StripeService();
